'use client';

import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { Button } from './Button';
import { Eyebrow } from './Eyebrow';
import { Rail } from './Rail';
import { useScanCount } from './ScanCount';
import styles from './interior.module.css';
import marketingStyles from './marketing.module.css';

interface CompactScanBaseProps {
  heading: string;
  description: string;
  /** The scan form. Every closing panel passes the hero's form (the `HomepageScanner` skin). */
  children: ReactNode;
  /**
   * Live number of businesses scanned. The homepage passes its own; interior
   * routes get it from their layout's `ScanCountBoundary`. With neither, the
   * count line is omitted – never replaced with a fallback number.
   */
  scanCount?: number;
  /** The section's id. `visibility-check` is the anchor the sticky scan bars scroll to. */
  id?: string;
  headingId?: string;
  className?: string;
}

type CompactScanProps = CompactScanBaseProps &
  (
    | {
        /** The sage card, for a scan that sits mid-page with content after it. */
        tone?: 'panel';
        /**
         * The Talk to us action. The caller owns it (`useTalkToUs(<location>)`), so
         * this primitive imports no analytics or modal context – the same boundary
         * `Faq` keeps for its CTA.
         */
        onTalkToUs: () => void;
      }
    | {
        /**
         * The full-bleed midnight band, for a scan that is the last thing on the
         * page. It runs straight into the midnight footer, which carries Talk to
         * us, so the band has none of its own.
         */
        tone: 'close';
        onTalkToUs?: never;
      }
  );

/**
 * The closing scan offer, in one of two tones. Only the heading and lead change
 * per page; the eyebrow, the form and the live count are the same everywhere,
 * so the offer reads as one thing across the site.
 *
 * - `panel` (default): the sage card inside the page rail
 *   (.claude/design-specs/2026-10-20-closing-scan-panel.html). Articles keep it
 *   because their scan sits above the related-links grids.
 * - `close`: the midnight band that ends the page and joins the footer
 *   (.claude/design-specs/2026-10-20-closing-band.html). It carries its own
 *   rail, so the caller must render it outside one.
 */
export function CompactScan(props: CompactScanProps): React.ReactElement {
  const {
    heading,
    description,
    children,
    scanCount,
    id = 'visibility-check',
    headingId = 'visibility-check-heading',
    className,
  } = props;
  const contextCount = useScanCount();
  const count = scanCount ?? contextCount;
  const countText =
    count !== null ? (
      <>
        <span className={styles.compactScanCount}>{count.toLocaleString('en-US')}</span> businesses
        scanned
      </>
    ) : null;
  const copy = (
    <div>
      <Eyebrow>Free visibility scan</Eyebrow>
      <h2 id={headingId}>{heading}</h2>
      <p className={styles.compactScanLead}>{description}</p>
    </div>
  );

  if (props.tone === 'close')
    return (
      <section
        id={id}
        aria-labelledby={headingId}
        className={clsx(marketingStyles.dark, styles.compactClose, className)}
        data-header-zone="dark"
      >
        <Rail>
          <div className={styles.compactCloseGrid}>
            {copy}
            <div className={styles.compactScanForm}>
              {children}
              {countText ? <p className={styles.compactCloseCount}>{countText}</p> : null}
            </div>
          </div>
        </Rail>
      </section>
    );

  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={clsx(styles.compactScan, className)}
      data-header-zone="tint"
    >
      {copy}
      <div className={styles.compactScanForm}>
        {children}
        <div className={styles.compactScanFoot}>
          {countText ? <p>{countText}</p> : null}
          <p>
            Have questions?{' '}
            {/* The text button inherits its ink, so the midnight sits on a wrapper
                rather than competing with `.textButton`'s `color: inherit`. */}
            <span className={styles.compactScanTalk}>
              <Button variant="text" aria-haspopup="dialog" onClick={props.onTalkToUs}>
                Talk to us
              </Button>
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}
